import _map from "lodash/map"

import CheckoutDiscountCode from "@src/components/CheckoutDiscountCode"
import CheckoutLineItem from "@src/components/CheckoutLineItem"

import { formatCurrency } from "@src/utilities/format-numbers"
import classNames from "@src/utilities/class-names"

import type { DeepPartial } from "@src/entities"
import type { ChangeEventHandler, MouseEventHandler } from "react"
import type { Cart } from "@src/entities/types/api"

interface CheckoutCartSummarDiscountsLineItemProps {
  amountInCents?: number
  code?: string | null
  onRemoveDiscount: MouseEventHandler<HTMLButtonElement>
}
function CheckoutCartSummarDiscountsLineItem({
  amountInCents,
  code,
  onRemoveDiscount,
}: CheckoutCartSummarDiscountsLineItemProps) {
  if (!amountInCents) {
    return null
  }
  return (
    <>
      <div className="col-span-2 text-gray-600">
        Discounts{" "}
        {code && (
          <>
            <span className="inline-block text-xs">({code})</span>
            <div className="mb-1">
              <button
                className="text-orange text-xs"
                onClick={onRemoveDiscount}
              >
                Remove
              </button>
            </div>
          </>
        )}
      </div>
      <div className="font-medium text-right">
        {formatCurrency(amountInCents ?? 0)}
      </div>
    </>
  )
}

interface CheckoutCartSummaryProps {
  cart: DeepPartial<Cart> | null | undefined
  discountCode: string
  discountCodeError: string | null
  onEnterDiscountCode: ChangeEventHandler<HTMLInputElement>
  onRemoveDiscountCode: MouseEventHandler<HTMLButtonElement>
  onSubmitDiscountCode: MouseEventHandler<HTMLButtonElement>
  salesTaxDescription: string | null
}
export default function CheckoutCartSummary({
  cart,
  discountCode,
  discountCodeError,
  onEnterDiscountCode,
  onSubmitDiscountCode,
  onRemoveDiscountCode,
  salesTaxDescription,
}: CheckoutCartSummaryProps) {
  if (!cart) {
    return null
  }
  return (
    <div className="max-w-sm">
      {_map(cart.lineItems, (lineItem, idx) => (
        <CheckoutLineItem
          key={`line_item_${idx}`}
          cartPrice={lineItem?.priceInCart}
          description={lineItem?.product?.description}
          frequencyInWeeks={lineItem?.frequencyInWeeks}
          name={lineItem?.product?.name ?? "Product"}
          retailPrice={lineItem?.product?.priceRetail}
          quantity={lineItem?.quantity}
          thumbnailUrl={lineItem?.product?.thumbnailUrl}
        />
      ))}
      {cart.allowDiscountCodes && (
        <div className="hidden md:block">
          <hr className="border-gray-300 my-8" />
          <CheckoutDiscountCode
            discountCode={discountCode}
            discountCodeError={discountCodeError}
            onEnterDiscountCode={onEnterDiscountCode}
            onSubmitDiscountCode={onSubmitDiscountCode}
          />
        </div>
      )}
      <hr className="border-gray-300 my-8" />
      <div className="grid grid-cols-3 gap-y-1 mb-8 text-sm">
        <div className="col-span-2 text-sm text-gray-600">Subtotal</div>
        <div className="text-sm font-medium text-right">
          {formatCurrency(cart.totals?.subtotal ?? 0)}
        </div>
        <CheckoutCartSummarDiscountsLineItem
          amountInCents={cart.totals?.discounts}
          code={cart.discountCode}
          onRemoveDiscount={onRemoveDiscountCode}
        />
        <div className="col-span-2 text-gray-600">Shipping</div>
        <div className="font-medium text-right">
          {cart.totals?.shipping
            ? formatCurrency(cart.totals.shipping)
            : "Free!"}
        </div>
        <div className="col-span-2 text-gray-600">
          Sales Tax
          {salesTaxDescription && (
            <p
              className="text-xs text-gray-600"
              dangerouslySetInnerHTML={{ __html: salesTaxDescription }}
            />
          )}
        </div>
        <div
          className={classNames(
            "text-right",
            salesTaxDescription ? "text-gray-600" : "font-medium text-black"
          )}
          dangerouslySetInnerHTML={{
            __html:
              !salesTaxDescription && cart.totals?.taxes > 0
                ? formatCurrency(cart.totals?.taxes ?? 0)
                : "&ndash;",
          }}
        />
        <div className="col-span-2 font-medium border-t border-gray-400 mt-1 pt-1 text-base">
          Total
        </div>
        <div className="font-medium border-t border-gray-400 mt-1 pt-1 text-base text-right">
          {formatCurrency(cart.totals?.total ?? 0)}
        </div>
      </div>
    </div>
  )
}
